import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 25;

export function MultiAowResultsModal({ results, onClose }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const filtered = useMemo(() => {
    if (!search) return results;
    const q = search.toLowerCase();
    return results.filter(r => {
      const res = r.result;
      if (!res) return false;
      return (res.resultCode?.toLowerCase().includes(q)) ||
             (res.title?.toLowerCase().includes(q)) ||
             (res.type?.toLowerCase().includes(q)) ||
             (res.initiative?.toLowerCase().includes(q)) ||
             r.aows.some(a => a.toLowerCase().includes(q));
    });
  }, [results, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageResults = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => { setPage(0); }, [search]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-5xl max-h-[85vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Results Contributing to Multiple Areas of Work ({results.length})
            </h3>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              These results are mapped to 2 or more AoWs and are counted in each.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-[var(--border-primary)]">
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" />
            <input
              type="text"
              placeholder="Search by code, title, type, AoW..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-[var(--border-primary)] rounded-lg bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-cgiar-green"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="bg-[var(--surface-secondary)] border-b border-[var(--border-primary)] sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Result Code</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Title</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Type</th>
                <th className="text-left px-3 py-2.5 font-medium text-[var(--text-secondary)]">Funding</th>
                <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Areas of Work</th>
              </tr>
            </thead>
            <tbody>
              {pageResults.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-[var(--text-tertiary)]">
                    {search ? 'No results match your search' : 'No multi-AoW results'}
                  </td>
                </tr>
              )}
              {pageResults.map((item, idx) => {
                const r = item.result;
                return (
                  <tr key={idx} className="border-t border-[var(--border-primary)] hover:bg-[var(--surface-hover)]">
                    <td className="px-4 py-2 font-mono text-[var(--text-secondary)]">{r?.resultCode || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-primary)] max-w-[280px] truncate" title={r?.title}>{r?.title || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{r?.type || '—'}</td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{r?.fundingType || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {item.aows.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map(aow => (
                          <span key={aow} className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
                            {aow}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-[var(--border-primary)] bg-[var(--surface-secondary)]">
            <span className="text-xs text-[var(--text-tertiary)]">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)] disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--text-secondary)] px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)] disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
